import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getDrones, getDeliveries, getMissions, getEvents, getAlerts } from '../api'
import FleetMap from '../components/FleetMap'
import './Dashboard.css'

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#f87171',
  high:     '#fb923c',
  medium:   '#fbbf24',
  low:      '#4ade80',
  info:     '#60a5fa',
}

export default function Dashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: drones = [], isLoading: dronesLoading } = useQuery({
    queryKey: ['drones'],
    queryFn: getDrones,
  })
  const { data: deliveries = [], isLoading: deliveriesLoading } = useQuery({
    queryKey: ['deliveries'],
    queryFn: getDeliveries,
  })
  const { data: missions = [], isLoading: missionsLoading } = useQuery({
    queryKey: ['missions'],
    queryFn: getMissions,
  })
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['events'],
    queryFn: getEvents,
  })

  const [alertTab, setAlertTab] = useState<'all'|'drone'|'agent'>('all')
  const { data: alertsData = [] } = useQuery<any[]>({ queryKey: ['alerts'], queryFn: () => getAlerts(), refetchInterval: 15000 })
  const filteredAlerts = alertsData.filter((a: any) => alertTab === 'all' || a.source === alertTab)

  const activeDrones    = (drones as any[]).filter(d => d.status === 'active' || d.status === 'online' || d.status === 'flying').length
  const offlineDrones   = (drones as any[]).filter(d => d.status === 'offline').length
  const activeMissions  = (missions as any[]).filter(m => m.status === 'in_progress' || m.status === 'pending' || m.status === 'active').length
  const criticalEvents  = (events as any[]).filter(e => e.severity === 'critical').length
  const recentEvents    = [...(events as any[])].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()).slice(0, 8)

  const loading = dronesLoading || deliveriesLoading || missionsLoading || eventsLoading

  return (
    <div className="dashboard">
      <h1>{t('dashboard.title')}</h1>

      {loading ? (
        <p className="dashboard-loading">טוען נתונים...</p>
      ) : (
        <>
          {/* ─── KPI Cards ─── */}
          <div className="dashboard-grid">
            <div className="dashboard-card" onClick={() => navigate('/drones')} style={{ cursor: 'pointer' }}>
              <h3>{t('dashboard.activeDrones')}</h3>
              <div className="card-value">{activeDrones}</div>
              <div className="card-sub">
                סה״כ: {(drones as any[]).length} | לא מחובר: {offlineDrones}
              </div>
            </div>
            <div className="dashboard-card" onClick={() => navigate('/tasks')} style={{ cursor: 'pointer' }}>
              <h3>{t('dashboard.totalDeliveries')}</h3>
              <div className="card-value">{(deliveries as any[]).length}</div>
              <div className="card-sub">משימות הובלה</div>
            </div>
            <div className="dashboard-card" onClick={() => navigate('/missions')} style={{ cursor: 'pointer' }}>
              <h3>{t('dashboard.activeMissions')}</h3>
              <div className="card-value">{activeMissions}</div>
              <div className="card-sub">סה״כ משימות: {(missions as any[]).length}</div>
            </div>
            <div
              className={`dashboard-card ${criticalEvents > 0 ? 'card-alert' : ''}`}
              style={{ cursor: criticalEvents > 0 ? 'pointer' : 'default' }}
            >
              <h3>{t('dashboard.criticalEvents')}</h3>
              <div className="card-value" style={{ color: criticalEvents > 0 ? '#f87171' : undefined }}>
                {criticalEvents}
              </div>
              <div className="card-sub">סה״כ אירועים: {(events as any[]).length}</div>
            </div>
          </div>

          {/* ─── Fleet Monitoring Map ─── */}
          <section className="dashboard-section">
            <div className="dashboard-section-header">
              <h2>🗺️ ניטור צי רחפנים</h2>
              <button className="dash-link-btn" onClick={() => navigate('/drones')}>
                צפה בכל הרחפנים →
              </button>
            </div>
            <FleetMap drones={drones as any[]} height={380} />
            <div className="fleet-legend">
              <span className="legend-item"><span className="legend-dot" style={{ background: '#4ade80' }} />מחובר / פעיל</span>
              <span className="legend-item"><span className="legend-dot" style={{ background: '#60a5fa' }} />טס</span>
              <span className="legend-item"><span className="legend-dot" style={{ background: '#fbbf24' }} />תחזוקה</span>
              <span className="legend-item"><span className="legend-dot" style={{ background: '#6b7280' }} />מנותק</span>
              <span className="legend-item"><span className="legend-dot" style={{ background: '#f87171' }} />שגיאה</span>
            </div>
          </section>

          {/* ─── Drone Status Table ─── */}
          {(drones as any[]).length > 0 && (
            <section className="dashboard-section">
              <div className="dashboard-section-header">
                <h2>✈ סטטוס צי</h2>
              </div>
              <div className="fleet-status-grid">
                {(drones as any[]).map((d: any) => (
                  <div
                    key={d.id}
                    className="fleet-status-card"
                    onClick={() => navigate(`/drones/${d.id}`)}
                  >
                    <div className="fsc-id">{d.drone_id}</div>
                    {d.tail_number && <div className="fsc-tail">זנב: {d.tail_number}</div>}
                    <span className={`status status-${d.status}`}>{d.status}</span>
                    {d.company_name && <div className="fsc-company">{d.company_name}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── Recent Events ─── */}
          <section className="dashboard-section">
            <div className="dashboard-section-header">
              <h2>⚠️ אירועים אחרונים</h2>
            </div>
            {recentEvents.length === 0 ? (
              <p className="dash-empty">אין אירועים.</p>
            ) : (
              <div className="events-list">
                {recentEvents.map((ev: any) => (
                  <div key={ev.id} className="event-row">
                    <span
                      className="event-severity"
                      style={{ background: SEVERITY_COLOR[ev.severity] + '22', color: SEVERITY_COLOR[ev.severity] }}
                    >
                      {ev.severity}
                    </span>
                    <span className="event-title">{ev.title || ev.message}</span>
                    <span className="event-time">
                      {ev.created_at ? new Date(ev.created_at).toLocaleString('he-IL') : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ─── Alerts Panel ─── */}
          <section className="dash-alerts-section">
            <div className="dash-alerts-header">
              <h2 className="dash-section-title">🔔 התראות מבצעיות</h2>
              <div className="dash-alerts-tabs">
                <button className={`dash-alert-tab ${alertTab==='all'?'active':''}`} onClick={()=>setAlertTab('all')}>הכל</button>
                <button className={`dash-alert-tab ${alertTab==='drone'?'active':''}`} onClick={()=>setAlertTab('drone')}>✈️ רחפן</button>
                <button className={`dash-alert-tab ${alertTab==='agent'?'active':''}`} onClick={()=>setAlertTab('agent')}>🤖 סוכן</button>
              </div>
            </div>
            <div className="dash-alerts-list">
              {filteredAlerts.length === 0 ? (
                <div className="dash-no-alerts">✅ אין התראות פעילות</div>
              ) : filteredAlerts.map((a: any) => (
                <div key={a.id} className={`dash-alert-item sev-${a.severity}`}>
                  <span className="dash-alert-src">{a.source === 'drone' ? '✈️' : '🤖'}</span>
                  <div className="dash-alert-body">
                    <div className="dash-alert-title">{a.title}</div>
                    {a.message && <div className="dash-alert-msg">{a.message}</div>}
                  </div>
                  <span className={`dash-alert-badge sev-${a.severity}`}>{a.severity}</span>
                  <span className="dash-alert-time">{a.created_at ? new Date(a.created_at).toLocaleTimeString('he-IL') : ''}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  )
}
